import { useQuery, useQueryClient } from '@tanstack/react-query';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateJourneyActions } from 'api/journey-actions/hooks';
import { useInvalidateJourneys } from 'api/journeys/hooks';
import { handleError } from 'api/query-client';

import {
  JourneyActionForm,
  createJourneyActionFormImage,
  getJourneyActionFormByJourneyActionId,
  loadForm,
  loadJourneyActionForm,
  loadJourneyActionForms,
  updateJourneyActionForm,
} from './requests';

const getJourneyActionFormKey = (formId: string) => ['journey-action-form', formId];

export const useInvalidateJourneyActionForms = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyActionForms = (formId?: string) =>
    queryClient.invalidateQueries(getJourneyActionFormKey(formId ?? ''));

  const invalidateJourneyActionForm = (formId?: string) =>
    queryClient.invalidateQueries(['journey-action-form', formId]);

  const invalidateJourneyActionFormByJourneyActionId = () =>
    queryClient.invalidateQueries(['journey-action-form-by-journey-action-id']);

  const invalidateJourneyActionFormsList = () =>
    queryClient.invalidateQueries(['journey-action-forms']);

  return {
    invalidateJourneyActionForms,
    invalidateJourneyActionFormByJourneyActionId,
    invalidateJourneyActionForm,
    invalidateJourneyActionFormsList,
  };
};

export const useJourneyActionForm = (formId: string) => {
  return useQuery({
    queryKey: getJourneyActionFormKey(formId),
    queryFn: () => loadJourneyActionForm(formId),
    enabled: Boolean(formId),
  });
};

export const useJourneyActionForms = (options?: {
  staleTime?: number;
  keepPreviousData?: boolean;
  includePublished?: boolean;
  journeyId?: string;
  travellerJourneyId?: string;
}) => {
  const includePublished = options?.includePublished ?? false;

  return useQuery({
    queryKey: [
      'journey-action-forms',
      {
        includePublished,
        journeyId: options?.journeyId,
        travellerJourneyId: options?.travellerJourneyId,
      },
    ],
    queryFn: () =>
      loadJourneyActionForms({
        includePublished,
        journeyId: options?.journeyId,
        travellerJourneyId: options?.travellerJourneyId,
      }),
    staleTime: options?.staleTime,
    keepPreviousData: options?.keepPreviousData,
  });
};

export const useUpdateJourneyActionForm = (
  formId: string,
  journeyActionId?: string,
  journeyId?: string
) => {
  const { t } = useScopedTranslate(locale);
  const {
    invalidateJourneyActionForm,
    invalidateJourneyActionForms,
    invalidateJourneyActionFormByJourneyActionId,
    invalidateJourneyActionFormsList,
  } = useInvalidateJourneyActionForms();
  const { invalidateJourneyAction } = useInvalidateJourneyActions();
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: (data: Partial<JourneyActionForm>) => updateJourneyActionForm(formId, data),
    onSuccess: async () => {
      await invalidateJourneyActionForm(formId);
      invalidateJourneyAction(journeyActionId ?? '');
      invalidateJourney(journeyId ?? '');
      invalidateJourneyActionForms();
      invalidateJourneyActionFormByJourneyActionId();
      invalidateJourneyActionFormsList();
      showSuccess(t('formUpdated'));
    },
    onError: () => {
      handleError(t('formUpdateFailed'));
    },
  });
};

export const useCreateJourneyActionFormImage = () => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: createJourneyActionFormImage,
    onError: () => {
      handleError(t('imageSaveFailed'));
    },
  });
};

export const useJourneyActionFormByJourneyActionId = (
  journeyActionId: string,
  travellerJourneyId?: string
) => {
  return useQuery({
    queryKey: ['journey-action-form-by-journey-action-id', journeyActionId, travellerJourneyId],
    queryFn: () => getJourneyActionFormByJourneyActionId(journeyActionId, travellerJourneyId),
    enabled: Boolean(journeyActionId),
  });
};

export const useForm = (shortId: string) => {
  return useQuery({
    queryKey: ['form', shortId],
    queryFn: () => loadForm(shortId),
  });
};

const locale = {
  en: {
    formUpdated: 'Form updated',
    formUpdateFailed: 'Failed to update form',
    imageSaveFailed: 'Failed to save image',
  },
};
